// src/store/api/centralAuthApi.ts
// Central Identity API endpoints via BeHeard backend proxy.
// Authentication is handled exclusively by Central via OIDC + PKCE.
// This API only handles refresh, logout, user info, and workspace selection.

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../index';
import {
  type CentralUser,
  type CentralWorkspace,
} from '../../config/centralAuth';

// ============================================================
// Configuration
// ============================================================

// BeHeard backend URL (where Central session proxy lives)
const API_URL = import.meta.env.VITE_API_URL || '/api/v1';
// Central session proxy is at /api/auth/central (not versioned)
const CENTRAL_SESSION_URL = API_URL.replace(/\/api\/v\d+$/, '/api/auth/central');

// ============================================================
// Types
// ============================================================

/**
 * Response from BeHeard backend proxy for refresh.
 * Note: refreshToken is NOT in response - cookie is rotated server-side.
 */
export interface CentralProxyRefreshResponse {
  accessToken: string;
  expiresIn: number;
}

// Re-export for convenience
export type { CentralUser, CentralWorkspace };

// ============================================================
// API Definition
// ============================================================

/**
 * Central Identity API via BeHeard backend proxy.
 *
 * SECURITY NOTES:
 * - Login/signup are handled exclusively by Central OIDC (no backend proxy)
 * - This API only handles token refresh, logout, user info, workspace selection
 * - Refresh token sent via httpOnly cookie (never exposed to JavaScript)
 */
export const centralAuthApi = createApi({
  reducerPath: 'centralAuthApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CENTRAL_SESSION_URL,
    credentials: 'include', // REQUIRED: sends httpOnly cookies
    prepareHeaders: (headers, { getState }) => {
      // Attach Central access token for authenticated requests (me, select-workspace)
      const centralToken = (getState() as RootState).centralAuth?.accessToken;
      if (centralToken) {
        headers.set('Authorization', `Bearer ${centralToken}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    /**
     * Refresh access token via BeHeard backend proxy.
     * Refresh token is sent automatically via httpOnly cookie.
     * NO request body needed - cookie is the credential.
     */
    centralRefresh: builder.mutation<CentralProxyRefreshResponse, void>({
      query: () => ({
        url: '/refresh',
        method: 'POST',
      }),
    }),

    /**
     * Logout via BeHeard backend proxy.
     * Clears httpOnly cookie and revokes token on Central.
     */
    centralLogout: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: '/logout',
        method: 'POST',
      }),
    }),

    /**
     * Get current user info from Central Identity.
     * Proxied through BeHeard backend.
     */
    centralMe: builder.query<CentralUser, void>({
      query: () => '/me',
    }),

    // Workspace switching is handled directly via Central (not through backend proxy).
    // See useWorkspace.ts for the 2-step flow:
    //   POST {CENTRAL_URL}/api/auth/select-workspace → new token
    //   GET  {CENTRAL_URL}/api/suite/switch-context   → context
  }),
});

// ============================================================
// Exports
// ============================================================

export const {
  useCentralRefreshMutation,
  useCentralLogoutMutation,
  useCentralMeQuery,
  useLazyCentralMeQuery,
} = centralAuthApi;
